import React from 'react';
import { Box, Fade } from '@mui/material';
import {
  CheckCircleOutline as CompleteIcon,
  Settings as ProcessingIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

interface IntermediateMessageProps {
  id: string;
  text: string;
  timestamp: string;
  type: 'info' | 'todo_start' | 'todo_complete' | 'warning' | 'error';
  metadata?: any;
}

export const IntermediateMessage: React.FC<IntermediateMessageProps> = ({
  text,
  type,
}) => {
  // Determine icon and color based on message type
  const getIcon = () => {
    switch (type) {
      case 'todo_complete':
        return <CompleteIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'todo_start':
        return <ProcessingIcon sx={{ fontSize: 16, color: 'primary.main' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />;
    }
  };

  // Get background color with opacity based on type
  const getBackgroundColor = () => {
    switch (type) {
      case 'todo_complete':
        return 'rgba(46, 125, 50, 0.08)'; // success with low opacity
      case 'todo_start':
        return 'rgba(25, 118, 210, 0.08)'; // primary with low opacity
      case 'warning':
        return 'rgba(237, 108, 2, 0.08)'; // warning with low opacity
      case 'error':
        return 'rgba(211, 47, 47, 0.08)'; // error with low opacity
      case 'info':
      default:
        return 'rgba(2, 136, 209, 0.08)'; // info with low opacity
    }
  };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          mb: 1,
          px: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: '70%',
            backgroundColor: getBackgroundColor(),
            borderRadius: 2,
            px: 2,
            py: 1,
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          >
            {getIcon()}
          </Box>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7em',
              color: 'text.secondary',
              fontStyle: 'italic',
              whiteSpace: 'pre-line', // Preserve line breaks
              lineHeight: 1.2,
              pr: 3, // Add padding to avoid overlap with icon
              '& p': {
                margin: 0,
                lineHeight: 1.2,
              },
              '& strong': {
                fontWeight: 600,
                fontStyle: 'normal',
              },
              '& ul, & ol': {
                margin: 0,
                paddingLeft: '1.5em',
                fontStyle: 'normal',
                lineHeight: 1.2,
              },
              '& li': {
                margin: 0,
                lineHeight: 1.2,
              },
              '& *:first-of-type': {
                marginTop: 0,
              },
              '& *:last-of-type': {
                marginBottom: 0,
              },
            }}
          >
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            >
              {text}
            </ReactMarkdown>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};
